import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject, tap, of, switchMap, map, catchError } from 'rxjs';
import { NavigationItem } from './navigation.types';
import { navigations as _navItems } from './navigation.data';
import { AuthService, DashboardService } from 'app/common/api-services';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<NavigationItem[]> =
        new ReplaySubject<NavigationItem[]>(1);

    private authService = inject(AuthService);
    private translocoService = inject(TranslocoService);
    private dashboardService = inject(DashboardService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<NavigationItem[]> {
        return this._navigation.asObservable().pipe(
            switchMap((navigationItems) => {
                return this.authService.user$.pipe(
                    switchMap((user) => {
                        if (!user || !user.roles) {
                            return of([]);
                        }

                        // Get preferred dashboards and inject them into navigation
                        return this.dashboardService.getPreferredDashboards().pipe(
                            map((preferredDashboards) => {
                                // Clone navigation items to avoid mutation
                                const navItems = JSON.parse(JSON.stringify(navigationItems));

                                // Inject preferred dashboards
                                const navWithDashboards = this.injectPreferredDashboards(navItems, preferredDashboards);

                                // Filter navigation items based on user claims
                                return this.filterNavigationItemsByAuth(navWithDashboards, user.roles);
                            }),
                            // Handle errors gracefully - if preferred dashboards fail to load, still show navigation
                            catchError(() => {
                                console.warn('Failed to load preferred dashboards for navigation');
                                return of(this.filterNavigationItemsByAuth(
                                    JSON.parse(JSON.stringify(navigationItems)),
                                    user.roles
                                ));
                            })
                        );
                    })
                );
            })
        );
    }

    /**
     * Helper method to inject preferred dashboards into navigation items
     * 
     * This method dynamically inserts user's preferred dashboards as children of the 
     * "dashboard" navigation item. Each dashboard is rendered as a navigation item 
     * with a star badge to indicate its preferred status.
     * 
     * @param items - The array of navigation items to modify (should be a deep clone)
     * @param dashboards - Array of preferred dashboard DTOs from the API
     * @returns Modified navigation items array with injected dashboard items
     * 
     * @remarks
     * - Dashboards are inserted only if the "dashboard" item exists
     * - Empty dashboard arrays are handled gracefully (returns original items)
     * - Dashboards are added at the end of the children array
     * - Each dashboard item includes:
     *   - Unique ID: `dashboard-{dashboardId}`
     *   - Title: Dashboard name or translated fallback
     *   - Icon: presentation-chart-line (heroicons)
     *   - Link: `/data-management/dashboards/{dashboardId}`
     *   - Badge: Orange star (★) indicator
     *   - Authorization: User and Admin roles
     * 
     * @example
     * ```typescript
     * const items = [...navigationItems];
     * const dashboards = [{ id: '1', name: 'Sales Dashboard', graphicsCount: 5 }];
     * const result = this.injectPreferredDashboards(items, dashboards);
     * // Result includes dashboard items as children of 'dashboard' element with star badge
     * ```
     */
    private injectPreferredDashboards(items: NavigationItem[], dashboards: any[]): NavigationItem[] {
        // Find the dashboard item to add dashboards as children
        const dashboardItem = items.find(item => item.id === 'dashboard');

        if (!dashboardItem || !dashboards || dashboards.length === 0) {
            return items;
        }

        // Create dashboard navigation items
        const dashboardItems: NavigationItem[] = dashboards.map(dashboard => ({
            id: `dashboard-${dashboard.id}`,
            title: dashboard.name || this.translocoService.translate('navigation.dashboard'),
            type: 'basic',
            icon: 'heroicons_outline:presentation-chart-line',
            link: `/data-management/dashboards/${dashboard.id}`,
            authorized: ['User', 'Admin'],
            badge: {
                title: '★',
                classes: 'px-2 bg-orange-600 text-white rounded-full'
            }
        }));

        // Initialize children array if it doesn't exist
        if (!dashboardItem.children) {
            dashboardItem.children = [];
        }

        // Add dashboards at the end of children
        dashboardItem.children.push(...dashboardItems);

        return items;
    }

    // Helper method to recursively filter navigation items
    private filterNavigationItemsByAuth(items: NavigationItem[], userClaims: string[]): NavigationItem[] {
        return items.filter(item => {
            // If no authorization required or user has required claim, keep item
            const isAuthorized = !item.authorized ||
                !item.authorized.length ||
                item.authorized.some(claim => userClaims.includes(claim));

            // For items with children, filter their children too
            if (isAuthorized && item.children) {
                item.children = this.filterNavigationItemsByAuth(item.children, userClaims);
            }

            return isAuthorized;
        });
    }

    // Helper method to recursively translate navigation items
    private translateNavigationItems(items: NavigationItem[]): NavigationItem[] {
        return items.map(item => {
            // Create a copy of the item to avoid mutating the original
            const translatedItem = { ...item };

            // Translate the title if it exists
            if (translatedItem.title) {
                // Use the item's id as the translation key, with fallback to original title
                const translationKey = `navigation.${translatedItem.id}`;
                translatedItem.title = this.translocoService.translate(translationKey) || translatedItem.title;
            }

            // Translate subtitle if it exists
            if (translatedItem.subtitle) {
                const translationKey = `navigation.${translatedItem.id}.subtitle`;
                translatedItem.subtitle = this.translocoService.translate(translationKey) || translatedItem.subtitle;
            }

            // Recursively translate children
            if (translatedItem.children) {
                translatedItem.children = this.translateNavigationItems(translatedItem.children);
            }

            return translatedItem;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<NavigationItem[]> {
        // Subscribe to language changes to update navigation titles
        this.translocoService.langChanges$.subscribe((lang) => {
            setTimeout(() => {
                const translatedNavItems = this.translateNavigationItems(_navItems);
                this._navigation.next(translatedNavItems);
            }, 100);
        });

        // Initial translation of navigation items
        return of(this.translateNavigationItems(_navItems)).pipe(
            tap((navigation) => this._navigation.next(navigation))
        );

        //return this.navigation$;
    }
}
